import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';

import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import type { Image as CraftImage } from '@core-mkt/interfaces/image';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { VisionModalService } from '@core-mkt/shared/components/vision-modal/vision-modal.service';
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

@Component({
  selector: 'top-section-carousel',
  templateUrl: './top-section-carousel.component.html',
  styleUrls: ['./top-section-carousel.component.scss'],
  standalone: true,
  imports: [],
})
export class TopSectionCarouselComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() carouselData: Array<{ topSectionCarouselImage: CraftImage[]; topSectionCarouselText: string }>;
  @Input() autoAnimate?: boolean;
  @ViewChild('swiperEl', { static: false }) swiperEl?: ElementRef<HTMLElement>;
  @ViewChild('paginationEl', { static: false }) paginationEl?: ElementRef<HTMLElement>;
  swiper?: Swiper;

  public swiperConfig: SwiperOptions = {
    modules: [Pagination, Autoplay],
    // The removed swiper/angular wrapper always set observer: true; Swiper 12 defaults it to
    // false. Restored so slides re-rendered after init are still picked up, matching v8.
    observer: true,
    pagination: {
      type: 'bullets',
      clickable: true,
    },
  };
  private _carouselSlides: Array<{ image: CraftImage; text: string }> = [];

  constructor(
    public imgixService: ImgixService,
    private modalService: VisionModalService,
    private breakpointObserver: BreakpointObserver,
    @Inject(PLATFORM_ID) private platformId: object,
    private ngZone: NgZone,
  ) {}

  get carouselSlides(): Array<{ image: CraftImage; text: string }> {
    return this._carouselSlides;
  }

  get hasCarousel(): boolean {
    return this._carouselSlides.length > 0;
  }

  ngOnInit(): void {
    if (this.carouselData && this.carouselData.length > 0) {
      this._carouselSlides = this.carouselData
        .map((slide) => ({
          image: slide.topSectionCarouselImage?.[0] || null,
          text: slide.topSectionCarouselText || '',
        }))
        .filter((slide) => slide.image !== null);

      if (this._carouselSlides.length > 0) {
        if (this.autoAnimate && this._carouselSlides.length > 1) {
          const prefersReducedMotion = this.breakpointObserver.isMatched('(prefers-reduced-motion: reduce)');

          if (!prefersReducedMotion) {
            this.swiperConfig = {
              ...this.swiperConfig,
              loop: true,
              autoplay: {
                delay: 7000,
                disableOnInteraction: false,
              },
            };
          }
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swiperEl) return;
    // Pagination lives outside the swiper root (see template) so it can be laid out
    // alongside the "View text version" button; hand over the element itself rather
    // than a selector string so the lookup stays scoped to this instance.
    // Swiper is constructed outside Angular's zone. The removed swiper/angular wrapper did
    // the same (swiper_angular.mjs), and it matters here: Swiper 12 registers pointermove /
    // touchmove listeners on `document`, and this app runs provideZoneChangeDetection() with
    // no event coalescing -- in-zone, every mouse move anywhere on the page would run a full
    // change-detection tick per carousel. Autoplay compounds it: in v12 it is a
    // requestAnimationFrame loop, so in-zone that is ~60 ticks/s while the hero is on screen.
    this.ngZone.runOutsideAngular(() => {
      this.swiper = new Swiper(this.swiperEl.nativeElement, {
        ...this.swiperConfig,
        pagination: { ...(this.swiperConfig.pagination as object), el: this.paginationEl?.nativeElement },
      });
    });
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
    this.swiper = undefined;
  }

  openCurrentSlideText(): void {
    const activeIndex = this.swiper?.realIndex ?? 0;
    const currentSlide = this._carouselSlides[activeIndex];

    if (currentSlide?.text) {
      this.modalService.openModal({
        showModal: true,
        modalContent: currentSlide.text,
      });
    }
  }
}
